import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { DirtinessService } from 'src/core/services/dirtiness.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MedicineService } from 'src/core/services/medicine.service';
import { 
  CreateMedicineDosagePackageTypePayload, 
  CreateMedicineDosageTypePayload, 
  CreateMedicinePayload, 
  DosageType, 
  DosageUnit, 
  Medicine, 
  MedicineDosagePackageType, 
  MedicineDosageType, 
  PackageType 
} from 'src/interfaces/medicine';

@Component({
    selector: 'app-medicine-create',
    templateUrl: './medicine-create.component.html',
    styleUrls: ['./medicine-create.component.scss'],
    standalone: false
})
export class MedicineCreateComponent implements OnInit {

  medicineForm = this._formBuilder.group({
    name: ['', Validators.required],
    description: ['', Validators.required],
    barcodePrefix: ['', Validators.required],
    activeIngredient: ['', Validators.required],
    activeIngredientNappyCode: [''],
    scheduleStatus: ['', Validators.required],
    canOrder: [false] 
  });

  scheduleStatuses: Array<number> = [0, 1, 2, 3, 4, 5, 6, 7, 8];

  section2Form = this._formBuilder.group({
    dosageUnitID: ['', Validators.required],
    dosageTypeID: ['', Validators.required],
    packageTypeID: ['', Validators.required]
  })



  medicineDosageTypeForm = this._formBuilder.group({
    additionalInformation: ['', Validators.required],
    usageInstructions: ['', Validators.required],
    storageDirections: ['', Validators.required],
    specialInstructions: [''],
    dosageStrength: ['', Validators.required],
    dosageType: ['', Validators.required],
    isActive: ['', Validators.required]
  });

  medicineDosagePackageTypeForm = this._formBuilder.group({
    containedQuantity: ['', Validators.compose([Validators.required, Validators.min(1)])],
    packageType: ['', Validators.required],
    labelName: ['', Validators.compose([Validators.required, Validators.maxLength(25)])],
    price: ['', Validators.compose([Validators.required, Validators.min(1)])]
  });



  @ViewChild('stepper') private _stepper: MatStepper;

  creatingMedicineIsEditable: boolean = true;
  creatingMedicineDosageTypeIsEditable: boolean = true;

  dosageUnits: DosageUnit[] = [];
  dosageTypes: DosageType[] = [];
  packageTypes: PackageType[] = [];
  currentDosageUnit: DosageUnit | null = null;
  currentDosageType: DosageType | null = null;

  createdMedicine: Medicine | null = null;
  createdMedicineDosageType: MedicineDosageType | null = null;
  createdMedicineDosagePackageType: MedicineDosagePackageType | null = null;

  medicineDosagePackageTypes: MedicineDosagePackageType[] = [];
  
  labelNameExceeds25Chars: boolean  = false;



  constructor(
    private _formBuilder: UntypedFormBuilder,
    private _medicineService: MedicineService,
    private _router: Router,
    private _dirtinessService: DirtinessService
  ) {

  }

  ngOnInit(): void {

    (async () => {
      this.dosageUnits = await this._medicineService.getDosageUnits(1, 10, '');
      this.dosageTypes = await this._medicineService.getDosageTypes(1, 10, '');
      this.packageTypes = await this._medicineService.getPackageTypes(1, 10, '');
    })();

    this.medicineDosagePackageTypeForm.controls['labelName']
    .valueChanges.subscribe((value) =>{
      this.labelNameExceeds25Chars = value.length > 25;
    })

  }

  async createMedicine() {

    if (!this.medicineForm.valid) return;

    const payload: CreateMedicinePayload = {
      name: this.medicineForm.value.name,
      description: this.medicineForm.value.description,
      active_ingredient: this.medicineForm.value.activeIngredient,
      active_ingredient_nappy_code: this.medicineForm.value.activeIngredientNappyCode ? this.medicineForm.value.activeIngredientNappyCode : undefined,
      schedule_status: this.medicineForm.value.scheduleStatus,
      barcode_prefix: this.medicineForm.value.barcodePrefix,
      can_be_ordered: this.medicineForm.value.canOrder
    }

    const medicine: Medicine | null = await this._medicineService
      .createMedicine(payload);

    if (medicine) {

      this.medicineForm
        .patchValue({
          medicine: medicine
        });

      this.createdMedicine = medicine;
      this.creatingMedicineIsEditable = false;
      this._dirtinessService.setDirty(true);
      this._stepper.next();
    }
  }

  async createDosageType(event: Event) {
    event.stopPropagation();
    const newDosageType: DosageType = await this._medicineService
      .openDosageTypeCreateDialog(
        "New dosage type",
        "Create new dosage type",
        this.dosageUnits
      );

    if (newDosageType) {
      // add dosage unit to the list dosage types
      this.dosageTypes.push(newDosageType);
      // clone array 
      this.dosageTypes = [...this.dosageTypes];
      // Update the form value to the newly created dosage type
      this.medicineDosageTypeForm.patchValue({
        dosageType: newDosageType
      });
    }
  }

  async createPackageType(event: Event) {
    event.stopPropagation();
    const newPackageType: PackageType = await this._medicineService
      .openPackageTypeCreateDialog(
        "New package type",
        "Create new package type"
      );

    if (newPackageType) {
      // add dosage unit to the list dosage types
      this.packageTypes.push(newPackageType);
      // clone array 
      this.packageTypes = [...this.packageTypes];
      // Update the form value to the newly created dosage type
      this.medicineDosagePackageTypeForm.patchValue({
        packageType: newPackageType
      });

    }
  }

  async createMedicineDosageType() {

    // if dosage type is null or medicine is null, cancel
    if (!this.createdMedicine) {
      alert("Please create medicine first");
      return;
    }



    const payload: CreateMedicineDosageTypePayload = {
      additional_information: this.medicineDosageTypeForm.value.additionalInformation,
      usage_instructions: this.medicineDosageTypeForm.value.usageInstructions,
      storage_directions: this.medicineDosageTypeForm.value.storageDirections,
      special_instructions: this.medicineDosageTypeForm.value.specialInstructions,
      dosage_strength: this.medicineDosageTypeForm.value.dosageStrength,
      dosage_type_id: this.medicineDosageTypeForm.value.dosageType.id,
      is_active: this.medicineDosageTypeForm.value.isActive
    };

    // create it 
    this.createdMedicineDosageType = await this._medicineService
      .createMedicineDosageType(this.createdMedicine.id, payload);

    if (this.createdMedicineDosageType) {
      this.creatingMedicineDosageTypeIsEditable = false;

      this.currentDosageType = this.medicineDosageTypeForm.value.dosageType;



      const currentDosageUnit: DosageUnit | undefined = this.dosageUnits
        .find(dosageUnit => {
          if (!this.currentDosageType) return false;
          return dosageUnit.id === this.currentDosageType.dosage_unit_id
        });

      this.currentDosageUnit = currentDosageUnit ? currentDosageUnit : null;
      this._stepper.next();
    }

  }

  async onDosagePackageTypesUpdated(updated: boolean) {
    if (!this.createdMedicine || !this.createdMedicineDosageType) return;
    if (updated) {
      this.medicineDosagePackageTypes = await this._medicineService
        .getMedicineDosagePackageTypes(
          this.createdMedicine.id,
          this.createdMedicineDosageType.id,
          1, 10, ''
        );
    }
  }

  async createMedicineDosagePackageType() {

    if (!this.createdMedicineDosageType || !this.createdMedicine) {
      alert("You have to select a package type. Medicine should have been created. A dosage should be linked to the medicine");
      return;
    }
    // create medicine dosage package type
    const payload: CreateMedicineDosagePackageTypePayload = {
      contained_quantity: this.medicineDosagePackageTypeForm.value.containedQuantity,
      package_type_id: this.medicineDosagePackageTypeForm.value.packageType.id,
      label_name: this.medicineDosagePackageTypeForm.value.labelName,
      price: this.medicineDosagePackageTypeForm.value.price
    }

    const medicineDosagePackageType: MedicineDosagePackageType | null = await this._medicineService
      .createMedicineDosagePackageType(
        this.createdMedicine.id,
        this.createdMedicineDosageType.id,
        payload
      );

    if (medicineDosagePackageType) {
      this.medicineDosagePackageTypes = await this._medicineService
        .getMedicineDosagePackageTypes(
          this.createdMedicine.id,
          this.createdMedicineDosageType.id,
          1, 10, ''
        )
      this.medicineDosagePackageTypeForm.reset();
    }
  }

  redirect() {
    if (!this.createdMedicine) return;
    this._dirtinessService.setDirty(false);
    this._router.navigate(['/portal/medicine/view', this.createdMedicine.id])
  }

}


